/**
 * The canvas body for a web page — Electron's `<webview>`, a real browser view
 * in its own process, which no site can refuse to render.
 *
 * DESKTOP ONLY, and there is no fallback renderer on purpose. An iframe was the
 * obvious one and it was worse than nothing: the sites you actually reach for
 * send `X-Frame-Options: SAMEORIGIN`, so the frame renders blank and reports
 * success, leaving no event to detect it and nothing on screen to explain it.
 * A browser tab does not need a browser inside it either — so the web client
 * opens a url as a TAB and never gets here (`canEmbedWebPages` is the gate, and
 * it keeps pages out of the canvas AND out of Home on that host).
 *
 * The chrome is deliberately minimal — back, forward, reload, the url, and the
 * way out. This is a viewer inside a project, not a browser: anything that
 * wants a password manager, an extension, or a login the app has no business
 * holding should leave for the real browser, and the last control is how.
 */

import type { Repo } from '@automerge/automerge-repo'
import { RepoContext } from '@automerge/automerge-repo-react-hooks'
import { ArrowLeft, ArrowRight, ExternalLink, RotateCw } from 'lucide-react'
import {
  createElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { forgetPage, isDriving, isTakenOver, watchDriving } from './driving'
import { handBackPage, takeOverPage } from './pageHandoff'
import { recordWebVisit } from './webHistory'

/** Hand a url to the real browser. The desktop shell recognises the `external`
 *  feature and calls openExternal; a browser just opens a tab, which is the same
 *  intent expressed in the only way it can be there. */
export function openInSystemBrowser(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer,external')
}

interface WebViewElement extends HTMLElement {
  src: string
  reload(): void
  goBack(): void
  goForward(): void
  canGoBack(): boolean
  canGoForward(): boolean
  getURL(): string
  getTitle(): string
  /** The guest's webContents id: how the shell knows this pane. */
  getWebContentsId(): number
}

/**
 * What the address bar was given, as a url to load. A bare host gets https; a
 * phrase with no dot in it is a search. Null for anything else that would not
 * be http(s), since this string ends up as the webview's src.
 */
export function addressToUrl(typed: string): string | null {
  const text = typed.trim()
  if (!text) return null
  if (/^https?:\/\//i.test(text)) {
    try {
      return new URL(text).href
    } catch {
      return null
    }
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(text) && !/^[^/\s]+:\d/.test(text))
    return null
  if (
    !/\s/.test(text) &&
    /^[^/\s]+\.[^/\s]+|^localhost(:\d+)?(\/|$)/i.test(text)
  )
    try {
      return new URL(`https://${text}`).href
    } catch {
      return null
    }
  return `https://www.google.com/search?q=${encodeURIComponent(text)}`
}

export function WebView({
  url,
  onNavigate,
}: {
  url: string
  /** Where the page went, so the pane can keep it across a remount. */
  onNavigate?: (url: string) => void
}) {
  return (
    <div className="flex h-full min-h-0 flex-col rounded-[inherit]">
      <EmbeddedPage url={url} onNavigate={onNavigate} />
    </div>
  )
}

function EmbeddedPage({
  url,
  onNavigate,
}: {
  url: string
  onNavigate?: (url: string) => void
}) {
  const ref = useRef<WebViewElement | null>(null)
  const onNavigateRef = useRef(onNavigate)
  onNavigateRef.current = onNavigate
  // Urls this view reported itself. One of them coming back as `url` is the
  // pane catching up with the page, not a request to go there: a redirect
  // chain reports several in one frame, and treating the stale ones as
  // requests would drag the page back a step.
  const reported = useRef(new Set<string>())
  const [current, setCurrent] = useState(url)
  const [nav, setNav] = useState({ back: false, forward: false })
  const [loading, setLoading] = useState(true)
  // The guest this pane is, as the shell knows it. Null until the guest has
  // attached: a <webview> throws on every method before then.
  const [guestId, setGuestId] = useState<number | null>(null)
  // Read without throwing: a pane outside a repo still takes over, it just has
  // no log to tell (RFC 0303 3e).
  const repo = useContext(RepoContext) as unknown as Repo | null

  // The element is created imperatively (React does not know `<webview>`), so
  // its events are bound the same way. Re-bound on nothing but mount: the
  // element persists across url changes, and re-creating it would drop the
  // page's history, which is the one thing a viewer must keep.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    let guest: number | null = null
    const learnGuest = () => {
      try {
        const id = el.getWebContentsId()
        if (Number.isInteger(id) && id !== guest) {
          guest = id
          setGuestId(id)
        }
      } catch {
        // Not attached yet; the next event asks again.
      }
    }
    const sync = () => {
      learnGuest()
      const at = el.getURL()
      setCurrent(at)
      setNav({ back: el.canGoBack(), forward: el.canGoForward() })
      if (/^https?:\/\//i.test(at)) {
        reported.current.add(at)
        onNavigateRef.current?.(at)
      }
    }
    const start = () => {
      learnGuest()
      setLoading(true)
    }
    const stop = () => {
      setLoading(false)
      sync()
      // On stop rather than on navigate, so the title is the page's and not
      // the url it shows while it is still arriving.
      recordWebVisit(el.getURL(), el.getTitle())
    }
    el.addEventListener('did-attach', learnGuest)
    el.addEventListener('dom-ready', learnGuest)
    el.addEventListener('did-start-loading', start)
    el.addEventListener('did-stop-loading', stop)
    el.addEventListener('did-navigate', sync)
    el.addEventListener('did-navigate-in-page', sync)
    return () => {
      el.removeEventListener('did-attach', learnGuest)
      el.removeEventListener('dom-ready', learnGuest)
      el.removeEventListener('did-start-loading', start)
      el.removeEventListener('did-stop-loading', stop)
      el.removeEventListener('did-navigate', sync)
      el.removeEventListener('did-navigate-in-page', sync)
      // The pane is going: what it said goes with it. Take over itself is the
      // shell's, on the page, and carried to a remount there.
      if (guest !== null) forgetPage(guest)
    }
  }, [])

  // Opening a DIFFERENT page on the canvas navigates the existing view rather
  // than remounting it, so the back button still leads where the user has been.
  useEffect(() => {
    const el = ref.current
    if (!el || reported.current.has(url)) return
    if (el.src !== url) el.src = url
  }, [url])

  const goTo = useCallback((typed: string) => {
    const el = ref.current
    const next = addressToUrl(typed)
    if (el && next) el.src = next
  }, [])

  const go = useCallback((fn: (el: WebViewElement) => void) => {
    if (ref.current) fn(ref.current)
  }, [])

  // Whether an agent is working in THIS page. Read from the store the shell
  // writes to after each action (RFC 0303 3e), so a page filling in by itself
  // always says who is doing it.
  const driven = useSyncExternalStore(
    watchDriving,
    () => guestId !== null && isDriving(guestId),
    () => false,
  )
  const held = useSyncExternalStore(
    watchDriving,
    () => guestId !== null && isTakenOver(guestId),
    () => false,
  )

  return (
    <>
      <WebChrome
        url={current}
        loading={loading}
        canBack={nav.back}
        canForward={nav.forward}
        onBack={() => go((el) => el.goBack())}
        onForward={() => go((el) => el.goForward())}
        onReload={() => go((el) => el.reload())}
        onGo={goTo}
        driven={driven}
        held={held}
        onTakeOver={() =>
          guestId !== null && takeOverPage(repo, guestId, current)
        }
        onHandBack={() =>
          guestId !== null && handBackPage(repo, guestId, current)
        }
      />
      {createElement('webview', {
        ref,
        src: url,
        // Its own partition: untrusted content must not read the app origin's
        // storage or service workers. Persisted, so a login survives a reopen.
        partition: 'persist:modweb',
        // `allowpopups` is deliberately absent: popups are refused here and the
        // shell routes them onto the canvas instead, so a target=_blank link
        // stays inside the project rather than spawning a stray window.
        //
        // Sized by inline style rather than a class: React does not know this
        // tag, and how it treats `className` on an element it does not know is
        // not something the layout should depend on.
        style: { display: 'flex', flex: '1 1 auto', minHeight: 0 },
      })}
    </>
  )
}

function WebChrome({
  url,
  loading,
  canBack = false,
  canForward = false,
  onBack,
  onForward,
  onReload,
  onGo,
  driven = false,
  held = false,
  onTakeOver,
  onHandBack,
}: {
  url: string
  loading: boolean
  canBack?: boolean
  canForward?: boolean
  onBack?: () => void
  onForward?: () => void
  onReload?: () => void
  /** The person typed an address and pressed Enter. */
  onGo?: (typed: string) => void
  driven?: boolean
  /** The person took this page back; the agent may not act on it. */
  held?: boolean
  onTakeOver?: () => void
  onHandBack?: () => void
}) {
  // What is in the box while the person is editing it; null when it should
  // just show where the page is. Held apart from `url` so a page navigating
  // underneath does not overwrite what they are halfway through typing.
  const [draft, setDraft] = useState<string | null>(null)
  return (
    <div className="flex h-8 shrink-0 items-center gap-1 border-border/60 border-b px-1.5">
      <ChromeButton title="Back" onClick={onBack} disabled={!canBack}>
        <ArrowLeft size={13} />
      </ChromeButton>
      <ChromeButton title="Forward" onClick={onForward} disabled={!canForward}>
        <ArrowRight size={13} />
      </ChromeButton>
      <ChromeButton title="Reload" onClick={onReload} disabled={!onReload}>
        <RotateCw size={13} className={loading ? 'animate-spin' : undefined} />
      </ChromeButton>
      <input
        type="text"
        aria-label="Address"
        title={url}
        value={draft ?? url}
        readOnly={!onGo}
        spellCheck={false}
        autoComplete="off"
        onFocus={(e) => {
          setDraft(url)
          e.currentTarget.select()
        }}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onBlur={() => setDraft(null)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && draft !== null) {
            onGo?.(draft)
            e.currentTarget.blur()
          } else if (e.key === 'Escape') {
            e.currentTarget.blur()
          }
        }}
        className="min-w-0 flex-1 truncate rounded bg-transparent px-1.5 py-0.5 text-[11px] text-muted-foreground outline-none focus:bg-muted focus:text-foreground"
      />
      {driven && (
        // Said here rather than over the page: the page is the thing being
        // read, and covering it would hide what the agent is working on.
        <span className="flex shrink-0 items-center gap-1.5 text-[11px]">
          <span className="text-muted-foreground">Agent is driving</span>
          <button
            type="button"
            onClick={onTakeOver}
            className="rounded border border-border/60 px-1.5 py-0.5 text-foreground hover:bg-muted"
          >
            Take over
          </button>
        </span>
      )}
      {held && (
        // The way back. Without it a page taken over once stayed off limits to
        // the agent for as long as the pane lived.
        <span className="flex shrink-0 items-center gap-1.5 text-[11px]">
          <span className="text-muted-foreground">You have this page</span>
          <button
            type="button"
            onClick={onHandBack}
            className="rounded border border-border/60 px-1.5 py-0.5 text-foreground hover:bg-muted"
          >
            Hand back
          </button>
        </span>
      )}
      <ChromeButton
        title="Open in browser (⌘⇧O)"
        onClick={() => openInSystemBrowser(url)}
      >
        <ExternalLink size={13} />
      </ChromeButton>
    </div>
  )
}

function ChromeButton({
  title,
  onClick,
  disabled = false,
  children,
}: {
  title: string
  onClick?: () => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled || !onClick}
      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
    >
      {children}
    </button>
  )
}
